import { FC, useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { EmptyPost } from "./emptyPost";
import { EyeIcon } from "@shared/assets";
import { TelegramMedia } from "./media";
import { TelegramFile } from "./file";
import { TelegramComment } from "./comment";
import { GetPostRes, ICreatePostForm } from "@entities/project";
import { DownloadAllBtn } from "../../../utils/downloadAllBtn";
import { CopyTextBtn } from "../../../utils/copyTextBtn";
import { preparePostsData } from "@entities/platform/ui/utils";
import { LinkPreview, extractSoleLink } from "./linkPreview";

interface DisplayTelegramProps {
  formState?: ICreatePostForm;
  platformId: number;
  post?: GetPostRes;
  orderId?: string;
}

export const DisplayTelegram: FC<DisplayTelegramProps> = ({
  formState,
  platformId,
  post,
  orderId,
}) => {
  const { mediaRes, textRes, fileRes, buttonsRes, commentRes } =
    preparePostsData(post);

  const currentPost = formState?.selectedMultiPostId
    ? formState?.multiposts?.find((item) =>
        orderId
          ? item?.order_id === orderId
          : item?.order_id === formState?.selectedMultiPostId,
      ) || {
        platform: platformId,
        files: [],
        media: [],
        buttons: [],
        order_id: formState?.selectedMultiPostId,
      }
    : (formState?.posts || []).find((item) => item.platform === platformId) || {
        platform: platformId,
        files: [],
        media: [],
        buttons: [],
      };
  const postText = currentPost?.text;
  const postMedia = currentPost?.media;
  const postFile = currentPost?.files;
  const postButtons = currentPost?.buttons;
  const postComment = currentPost?.comment;

  const formSoleLink = !postMedia?.length
    ? extractSoleLink(postText?.[0]?.content)
    : null;
  const resSoleLink = !mediaRes?.length ? extractSoleLink(textRes) : null;

  const imgRef = useRef<HTMLImageElement>(null);
  const lastImgWidthRef = useRef<number | null>(null);
  const [resizes, setResizes] = useState<{
    borderRadius: number;
    timeSize: number;
    channelNameSize: number;
    channelSubsSize: number;
    avatarWidthSize: number;
    unmuteSize: number;
    displayTopSize: number;
    displayBottomSize: number;
    downloadIconSize: number;
  } | null>(null);

  useEffect(() => {
    const imgElement = imgRef.current;
    if (!imgElement) return;

    const updateSizes = () => {
      const imgWidth = Math.round(imgElement.offsetWidth);
      if (lastImgWidthRef.current === imgWidth) return;
      lastImgWidthRef.current = imgWidth;

      setResizes({
        borderRadius: (imgWidth / 364) * 54,
        timeSize: (imgWidth / 364) * 14,
        channelNameSize: (imgWidth / 364) * 12,
        channelSubsSize: (imgWidth / 364) * 10,
        avatarWidthSize: (imgWidth / 364) * 30,
        unmuteSize: (imgWidth / 364) * 14,
        displayTopSize: (imgWidth / 364) * 80,
        displayBottomSize: (imgWidth / 364) * 60,
        downloadIconSize: (imgWidth / 364) * 20,
      });
    };

    const observer = new ResizeObserver(updateSizes);
    observer.observe(imgElement);
    updateSizes();

    return () => observer.disconnect();
  }, []);

  return (
    <div className={styles.screen_wrapper}>
      <div className={styles.screen}>
        <p
          className={styles.time}
          style={{ fontSize: `${resizes?.timeSize}px` }}
        >
          9:41
        </p>
        <img
          className={styles.statusbar}
          src="/images/phoneDisplay/statusbar.svg"
        />
        <img
          className={styles.dynamic}
          src="/images/phoneDisplay/dynamic.png"
        />
        <img className={styles.back} src="/images/phoneDisplay/back.svg" />
        <div className={styles.channel}>
          <p
            className={styles.channel__name}
            style={{ fontSize: `${resizes?.channelNameSize}px` }}
          >
            Channel name
          </p>
          <p
            className={styles.channel__subs}
            style={{ fontSize: `${resizes?.channelSubsSize}px` }}
          >
            1 312 678 subscribers
          </p>
        </div>
        <div
          className={styles.avatar}
          style={{
            width: `${resizes?.avatarWidthSize}px`,
            height: `${resizes?.avatarWidthSize}px`,
          }}
        ></div>
        <img
          ref={imgRef}
          className={styles.mockup}
          src="/images/phoneDisplay/iphonescreen.png"
        />
        <div
          className={styles.unmute}
          style={{ fontSize: `${resizes?.unmuteSize}px` }}
        >
          Unmute
        </div>
        {formState ? (
          <div
            className={styles.display}
            style={{
              borderRadius: `${resizes?.borderRadius}px`,
              paddingTop: `${resizes?.displayTopSize}px`,
              paddingBottom: `${resizes?.displayBottomSize}px`,
            }}
          >
            {(postText && postText[0]?.content !== "<p></p>") ||
            postMedia?.length ||
            postComment ||
            postFile?.length ||
            postButtons?.length ? (
              <div className={styles.content}>
                <div className={styles.post}>
                  {postMedia && postMedia?.length > 0 && (
                    <TelegramMedia
                      medias={postMedia}
                      iconSize={resizes?.downloadIconSize || 20}
                    />
                  )}
                  <div
                    className={`${styles.post__text} post_pasted_link`}
                    dangerouslySetInnerHTML={{
                      __html: (postText && postText[0]?.content) || "",
                    }}
                    style={{ fontSize: `${resizes?.timeSize}px` }}
                  />
                  {formSoleLink && (
                    <div
                      className={styles.post__preview}
                      style={{ fontSize: `${resizes?.timeSize}px` }}
                    >
                      <LinkPreview url={formSoleLink} />
                    </div>
                  )}
                  <div className={styles.info}>
                    <EyeIcon />
                    <span>213,7K</span>
                    <span>19:00</span>
                  </div>
                </div>
                {postButtons && postButtons?.length > 0 && (
                  <div className={styles.buttons}>
                    {postButtons?.map((button, index) => (
                      <a
                        key={index}
                        href={button?.url}
                        target="_blank"
                        className="truncate"
                        style={{ fontSize: `${resizes?.timeSize}px` }}
                      >
                        {button?.content}
                      </a>
                    ))}
                  </div>
                )}
                {postFile?.length && (
                  <TelegramFile
                    file={postFile[0]}
                    fontSize={resizes?.timeSize || 14}
                  />
                )}
                {postComment && (
                  <TelegramComment
                    comment={postComment}
                    fontSize={resizes?.timeSize || 14}
                  />
                )}
                <div className={styles.stroke}></div>
              </div>
            ) : (
              <EmptyPost />
            )}
          </div>
        ) : (
          <div
            className={styles.display}
            style={{
              borderRadius: `${resizes?.borderRadius}px`,
              paddingTop: `${resizes?.displayTopSize}px`,
              paddingBottom: `${resizes?.displayBottomSize}px`,
            }}
          >
            {(textRes && textRes !== "<p></p>") ||
            mediaRes?.length ||
            commentRes ||
            fileRes ||
            buttonsRes?.length ? (
              <div className={styles.content}>
                <div className={styles.post}>
                  {mediaRes && mediaRes?.length > 0 && (
                    <TelegramMedia
                      mediasRes={mediaRes}
                      iconSize={resizes?.downloadIconSize || 20}
                    />
                  )}
                  <div
                    className={`${styles.post__text} post_pasted_link`}
                    dangerouslySetInnerHTML={{
                      __html: textRes || "",
                    }}
                    style={{ fontSize: `${resizes?.timeSize}px` }}
                  />
                  {resSoleLink && (
                    <div
                      className={styles.post__preview}
                      style={{ fontSize: `${resizes?.timeSize}px` }}
                    >
                      <LinkPreview url={resSoleLink} />
                    </div>
                  )}

                  <div className={styles.info}>
                    <EyeIcon />
                    <span>213,7K</span>
                    <span>19:00</span>
                  </div>
                </div>
                {buttonsRes && buttonsRes?.length > 0 && (
                  <div className={styles.buttons}>
                    {buttonsRes?.map((button, index) => (
                      <a
                        key={index}
                        href={button?.url}
                        target="_blank"
                        className="truncate"
                        style={{ fontSize: `${resizes?.timeSize}px` }}
                      >
                        {button?.content}
                      </a>
                    ))}
                  </div>
                )}
                {fileRes && (
                  <TelegramFile
                    file={fileRes}
                    fontSize={resizes?.timeSize || 14}
                  />
                )}
                {commentRes && (
                  <TelegramComment
                    comment={commentRes}
                    fontSize={resizes?.timeSize || 14}
                  />
                )}
                <div className={styles.stroke}></div>
              </div>
            ) : (
              <EmptyPost />
            )}
          </div>
        )}
      </div>
      <section className="grid grid-cols-[1fr_auto] mt-2 ml-2.5 gap-1.5">
        {(!!fileRes || !!mediaRes?.length) && (
          <DownloadAllBtn
            post={post}
            formState={formState}
            currentPost={currentPost}
          />
        )}
        {!!textRes && <CopyTextBtn text={textRes} />}
      </section>
    </div>
  );
};
